import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import UsedInMultiSelect from './UsedInMultiSelect';
import styles from './MixModal.module.css';

// Add / Edit modal for a concrete mix design. `mix` null = add new,
// otherwise we're editing. Used-In is a multi-select of project-managed
// options (a single design can be valid for multiple elements — e.g.
// Foundations + Slabs); numeric fields use type=number so iPad keypads
// behave.

// Coerce whatever the row currently holds into a clean string array.
// Old single-string used_in values become ['value']; nullish becomes [].
function toUsedInArray(v) {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === 'string' && v) return [v];
  return [];
}

export default function MixModal({ mix, usedInOptions, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    mix_id:        mix?.mix_id        || '',
    description:   mix?.description   || '',
    used_in:       toUsedInArray(mix?.used_in),
    slump_spread:  mix?.slump_spread  ?? '',
    air_pct:       mix?.air_pct       ?? '',
    wcm:           mix?.wcm           ?? '',
    strength_psi:  mix?.strength_psi  ?? '',
  }));
  const [saving, setSaving] = useState(false);
  const isEdit = !!mix;

  function patch(p) { setForm((f) => ({ ...f, ...p })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.mix_id.trim()) {
      toast.error('Mix ID is required');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        mix_id:       form.mix_id.trim(),
        description:  form.description.trim() || null,
        // Always send the array — the server normalises empty → NULL.
        used_in:      form.used_in,
        slump_spread: form.slump_spread === '' ? null : Number(form.slump_spread),
        air_pct:      form.air_pct === '' ? null : Number(form.air_pct),
        wcm:          form.wcm === '' ? null : Number(form.wcm),
        strength_psi: form.strength_psi === '' ? null : Math.round(Number(form.strength_psi)),
      });
      // Parent closes the modal on successful save.
    } catch (_) {
      // onSave reports its own toast.
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <form className={styles.modal} onSubmit={handleSubmit}>
        <div className={styles.header}>
          <span>{isEdit ? `Edit mix · ${mix.mix_id}` : 'Add mix'}</span>
          <button type="button" className={styles.closeBtn} onClick={onClose}>×</button>
        </div>
        <div className={styles.body}>
          <Field label="Mix ID *">
            <input
              className={styles.input}
              value={form.mix_id}
              onChange={(e) => patch({ mix_id: e.target.value })}
              autoFocus
              placeholder="e.g. 4000-A"
            />
          </Field>
          <Field label="Description">
            <input
              className={styles.input}
              value={form.description}
              onChange={(e) => patch({ description: e.target.value })}
              placeholder="Short description"
            />
          </Field>
          <Field label="Used In (one or more)">
            <UsedInMultiSelect
              options={usedInOptions.map((o) => o.name)}
              value={form.used_in}
              onChange={(arr) => patch({ used_in: arr })}
            />
            {usedInOptions.length === 0 && (
              <div className={styles.hint}>
                No options yet — add them in Project Settings → Used In.
              </div>
            )}
          </Field>

          <div className={styles.numGrid}>
            <Field label="Slump / Spread (in)">
              <input
                type="number" inputMode="decimal" step="0.25"
                className={styles.input}
                value={form.slump_spread}
                onChange={(e) => patch({ slump_spread: e.target.value })}
              />
            </Field>
            <Field label="Air (%)">
              <input
                type="number" inputMode="decimal" step="0.1"
                className={styles.input}
                value={form.air_pct}
                onChange={(e) => patch({ air_pct: e.target.value })}
              />
            </Field>
            <Field label="W/CM">
              <input
                type="number" inputMode="decimal" step="0.01"
                className={styles.input}
                value={form.wcm}
                onChange={(e) => patch({ wcm: e.target.value })}
              />
            </Field>
            <Field label="Strength (PSI) *">
              <input
                type="number" inputMode="numeric" step="50"
                className={styles.input}
                value={form.strength_psi}
                onChange={(e) => patch({ strength_psi: e.target.value })}
              />
            </Field>
          </div>
        </div>
        <div className={styles.footer}>
          <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className={styles.btnPrimary} disabled={saving || !form.mix_id.trim()}>
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Add mix')}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>{label}</label>
      {children}
    </div>
  );
}
